import { useState, useEffect } from 'react'
import { useIsMobile } from '@/hooks/use-mobile'
import { authService } from '@/services/authService'
import { getPendingInviteRedirect } from '@/utils/pendingInvite'
import { SignupScreenDesktop } from './SignupScreenDesktop'
import { SignupScreenMobile } from './SignupScreenMobile'
import { SignupTypeSelectionScreen } from './SignupTypeSelectionScreen'
import { getDefaultSignupFormData, type SignupScreenBaseProps } from './SignupScreenBase'
import type { AccountType } from './SignupTypeSelectionScreen'

const ACCOUNT_TYPE_STORAGE_KEY = 'signup_account_type'

/**
 * SignupScreen component - Multi-step flow
 * Step 1: Signup Form (email, password, terms)
 * Step 2: Account Type Selection (Individual vs Institution)
 * 
 * Renders mobile or desktop version based on screen size
 * 
 * Uses component split pattern for different layouts:
 * - Mobile: Compact layout with vertical spacing
 * - Desktop: Standard layout with horizontal spacing
 * 
 * Manages form state, validation, password strength, and submission logic
 */
const SignupScreen = () => {
  const isMobile = useIsMobile()
  const [currentStep, setCurrentStep] = useState<'signup' | 'type-selection'>('signup')
  const [formData, setFormData] = useState(getDefaultSignupFormData())
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [acceptTerms, setAcceptTerms] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Check if user is authenticated and handle routing accordingly
  useEffect(() => {
    const isAuthenticated = authService.isAuthenticated()
    const hasSelectedAccountType = !!localStorage.getItem(ACCOUNT_TYPE_STORAGE_KEY)
    const pendingInviteRedirect = getPendingInviteRedirect()

    if (isAuthenticated) {
      // An invitee joins an existing organization - skip the account-type funnel
      // entirely and take them straight back to acceptance.
      if (pendingInviteRedirect) {
        window.location.replace(pendingInviteRedirect)
        return
      }
      // If authenticated but hasn't selected account type, show type selection
      if (!hasSelectedAccountType) {
        setCurrentStep('type-selection')
      } else {
        // If account type already selected, redirect to attestation
        window.location.replace('/attestation')
      }
    }
  }, [])

  const clearField = (field: 'email' | 'password' | 'confirmPassword') => {
    setFormData(prev => ({ ...prev, [field]: '' }))
  }

  const handleFormDataChange = (updates: Partial<typeof formData>) => {
    setFormData(prev => ({ ...prev, ...updates }))
    // Clear error when user starts typing
    if (error) setError('')
  }

  const validateForm = () => {
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match')
      return false
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long')
      return false
    }

    if (!acceptTerms) {
      setError('Please accept the Terms of Service and Privacy Policy')
      return false
    }

    return true
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    if (!validateForm()) {
      setLoading(false)
      return
    }

    try {
      const { confirmPassword, ...signupData } = formData
      await authService.signup(signupData)
      // An invitee joining an existing organization must not create one of their
      // own - send them back to accept the invitation instead.
      const pendingInviteRedirect = getPendingInviteRedirect()
      if (pendingInviteRedirect) {
        window.location.replace(pendingInviteRedirect)
        return
      }
      // After successful signup, move to account type selection
      setCurrentStep('type-selection')
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleAccountTypeSelect = (type: AccountType) => {
    // Store account type in localStorage for attestation flow
    localStorage.setItem(ACCOUNT_TYPE_STORAGE_KEY, type)
    // Redirect to attestation (which will route to appropriate form based on type)
    // Use replace to prevent back navigation
    window.location.replace('/attestation')
  }

  // Show signup form first, then account type selection
  if (currentStep === 'type-selection') {
    return <SignupTypeSelectionScreen onSelect={handleAccountTypeSelect} />
  }

  const baseProps: SignupScreenBaseProps = {
    formData,
    showPassword,
    showConfirmPassword,
    acceptTerms,
    loading,
    error,
    onFormDataChange: handleFormDataChange,
    onShowPasswordChange: setShowPassword,
    onShowConfirmPasswordChange: setShowConfirmPassword,
    onAcceptTermsChange: setAcceptTerms,
    onClearField: clearField,
    onSubmit: handleSubmit,
  }

  // Component Split pattern - completely separate mobile and desktop rendering
  if (isMobile) {
    return <SignupScreenMobile {...baseProps} />
  }

  return <SignupScreenDesktop {...baseProps} />
}

export default SignupScreen

